import { apiUrl } from '../config';
import {
  type Avatar,
  type Equipped,
  type LeaderboardPage,
  type LeaderboardWindow,
  type Player,
  type PublicPlayer,
  type ResolveResult,
  type ScanResult,
  parseAvatar,
  parseLeaderboardPage,
  parseLeaderboardWindow,
  parsePlayer,
  parsePublicPlayer,
  parseResolveResult,
  parseScanResult,
} from '../models/types';
import { Logger } from './logger';

/** Typed error from the API. `code` is the machine-readable `error.code` the server sends. */
export class ApiException extends Error {
  constructor(
    readonly statusCode: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiException';
  }
  get isCooldown() {
    return this.code === 'cooldown';
  }
  get isAccuracyTooLow() {
    return this.code === 'accuracy_too_low';
  }
  get isFindNotFound() {
    return this.code === 'find_not_found';
  }
  get isHuntExpired() {
    return this.code === 'hunt_expired';
  }
  get isInvalidCursor() {
    return this.code === 'invalid_cursor';
  }
  get isInvalidAvatar() {
    return this.code === 'invalid_avatar';
  }
  get isPlayerNotFound() {
    return this.code === 'player_not_found';
  }
  get isUnauthorized() {
    return this.statusCode === 401 || this.statusCode === 403;
  }
  get retryAfterSeconds(): number | undefined {
    const n = Number.parseInt(this.details.retryAfterSeconds ?? '', 10);
    return Number.isNaN(n) ? undefined : n;
  }
}

type Json = Record<string, unknown>;

export class ApiClient {
  private readonly log = new Logger('ApiClient');

  constructor(
    private readonly getToken: () => Promise<string>,
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  async getPlayer(): Promise<Player> {
    return parsePlayer(await this.send('GET', 'api/v1/player'));
  }

  async updateDisplayName(displayName: string): Promise<Player> {
    return parsePlayer(await this.send('PUT', 'api/v1/player', { displayName }));
  }

  /** Replaces the whole equipped set; slots not in `equipped` are emptied. */
  async putAvatar(equipped: Equipped): Promise<Avatar> {
    const json = await this.send('PUT', 'api/v1/player/avatar', { equipped });
    return parseAvatar(json.avatar);
  }

  /** Another player's public profile. Throws `isPlayerNotFound` on 404. */
  async getPublicPlayer(playerId: string): Promise<PublicPlayer> {
    return parsePublicPlayer(await this.send('GET', `api/v1/players/${encodeURIComponent(playerId)}`));
  }

  async scan(p: { lat: number; lng: number; accuracy: number }): Promise<ScanResult> {
    return parseScanResult(await this.send('POST', 'api/v1/scan', p));
  }

  async resolveFind(findId: string, opts: { pickup: boolean }): Promise<ResolveResult> {
    return parseResolveResult(await this.send('POST', `api/v1/finds/${findId}`, { action: opts.pickup ? 'pickup' : 'leave' }));
  }

  /** The window around the signed-in player (10 above, them, 10 below). */
  async getLeaderboard(): Promise<LeaderboardWindow> {
    return parseLeaderboardWindow(await this.send('GET', 'api/v1/leaderboard'));
  }

  /** The next 10 entries beyond `cursor`, towards rank 1 when `up` is true. */
  async getLeaderboardPage(cursor: string, opts: { up: boolean }): Promise<LeaderboardPage> {
    return parseLeaderboardPage(
      await this.send('GET', `api/v1/leaderboard?cursor=${encodeURIComponent(cursor)}&direction=${opts.up ? 'up' : 'down'}`),
    );
  }

  private async send(method: string, path: string, body?: Json): Promise<Json> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${await this.getToken()}`,
      Accept: 'application/json',
    };
    if (body) headers['Content-Type'] = 'application/json';

    this.log.debug(`→ ${method} ${path}`);
    const response = await this.fetchImpl(apiUrl(path), { method, headers, body: body ? JSON.stringify(body) : undefined });
    this.log.debug(`← ${method} ${path} ${response.status}`);

    const text = await response.text();
    let decoded: unknown = {};
    if (text) {
      try {
        decoded = JSON.parse(text);
      } catch {
        decoded = {};
      }
    }
    if (response.ok) return decoded as Json;

    const err = ((decoded as Json).error ?? undefined) as Json | undefined;
    const details: Record<string, string> = {};
    for (const [k, v] of Object.entries((err?.details as Json | undefined) ?? {})) details[k] = String(v);
    throw new ApiException(
      response.status,
      (err?.code as string | undefined) ?? `http_${response.status}`,
      (err?.message as string | undefined) ?? (response.statusText || 'Request failed'),
      details,
    );
  }
}
